import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Background,
  ConnectionMode,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";

import type {
  RepoGraphBusiness,
  RepoGraphEdge,
  RepoGraphNode,
  RepoGraphResponse,
} from "@oracus/shared/api/repo-graph";

import { useRepoGraph } from "../api/repo-graph.api.ts";

type EdgeKind = "http" | "rpc" | "event" | "library" | "shared-db";

const EDGE_KINDS: EdgeKind[] = ["http", "rpc", "event", "library", "shared-db"];

const EDGE_COLORS: Record<string, string> = {
  http: "#10b981", // green
  rpc: "#3b82f6", // blue
  event: "#f59e0b", // amber
  library: "#71717a", // zinc-500
  "shared-db": "#ef4444", // red — usually an anti-pattern, worth flagging
};

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;

/** Run dagre layout to assign x/y positions to nodes given the edge set. */
function layout(
  nodes: RepoGraphNode[],
  edges: RepoGraphEdge[],
): Map<string, { x: number; y: number }> {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: 40, ranksep: 100 });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of nodes) {
    g.setNode(n.slug, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const e of edges) {
    g.setEdge(e.sourceSlug, e.targetSlug);
  }
  dagre.layout(g);

  const positions = new Map<string, { x: number; y: number }>();
  for (const n of nodes) {
    const { x, y } = g.node(n.slug);
    // Dagre returns center coords; xyflow wants top-left.
    positions.set(n.slug, {
      x: x - NODE_WIDTH / 2,
      y: y - NODE_HEIGHT / 2,
    });
  }
  return positions;
}

function buildFlowData(
  graph: RepoGraphResponse,
  enabledKinds: Set<EdgeKind>,
  view: DetailView,
): { nodes: Node[]; edges: Edge[] } {
  const filteredEdges = graph.edges.filter((e) =>
    enabledKinds.has(e.kind as EdgeKind),
  );
  const positions = layout(graph.nodes, filteredEdges);

  const nodes: Node[] = graph.nodes.map((n) => ({
    id: n.slug,
    position: positions.get(n.slug) ?? { x: 0, y: 0 },
    data: { repo: n, view },
    type: "repo",
    width: NODE_WIDTH,
    height: NODE_HEIGHT,
  }));

  // De-dupe parallel edges (same source/target/kind) by keying with kind.
  // Multiple edges between the same pair are common — e.g. shared library +
  // HTTP call. Show them all but offset visually.
  const edges: Edge[] = filteredEdges.map((e, i) => {
    const color = EDGE_COLORS[e.kind] ?? "#a1a1aa";
    return {
      id: `${e.sourceSlug}->${e.targetSlug}::${e.kind}::${i}`,
      source: e.sourceSlug,
      target: e.targetSlug,
      label: e.kind,
      labelStyle: { fontSize: 10, fill: "#52525b" },
      labelBgStyle: { fill: "white", fillOpacity: 0.9 },
      style: { stroke: color, strokeWidth: 1.5 },
      animated: e.kind === "http" || e.kind === "rpc",
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color,
        width: 16,
        height: 16,
      },
      data: { edge: e },
    };
  });

  return { nodes, edges };
}

function RepoNode({
  data,
}: {
  data: { repo: RepoGraphNode; view: DetailView };
}) {
  const r = data.repo;
  return (
    <div
      className={`relative rounded-md border bg-white px-3 py-2 shadow-sm ${
        r.hasManifest ? "border-zinc-300" : "border-dashed border-zinc-300 opacity-60"
      }`}
      style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: "#a1a1aa",
          width: 8,
          height: 8,
          border: "none",
        }}
      />
      <div className="truncate text-sm font-medium text-zinc-900">{r.name}</div>
      <div className="truncate text-[11px] text-zinc-500">{r.slug}</div>
      {data.view === "technical" ? (
        <TechnicalBadges repo={r} />
      ) : (
        <BusinessBadges repo={r} />
      )}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "#a1a1aa",
          width: 8,
          height: 8,
          border: "none",
        }}
      />
    </div>
  );
}

function TechnicalBadges({ repo: r }: { repo: RepoGraphNode }) {
  const deploymentBadge =
    r.deploymentKind === "kubernetes"
      ? { label: "k8s", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" }
      : r.deploymentKind === "docker-compose"
        ? { label: "compose", tone: "bg-sky-50 text-sky-700 border-sky-200" }
        : r.deploymentKind === "dockerfile"
          ? {
              label: "docker",
              tone: "bg-zinc-50 text-zinc-700 border-zinc-200",
            }
          : null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {r.teamName && (
        <span className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] text-zinc-700">
          {r.teamName}
        </span>
      )}
      {r.language && (
        <span className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] text-zinc-700">
          {r.language}
        </span>
      )}
      {deploymentBadge && (
        <span
          className={`rounded border px-1.5 py-0.5 text-[10px] ${deploymentBadge.tone}`}
        >
          {deploymentBadge.label}
        </span>
      )}
    </div>
  );
}

/**
 * Business-view badges on a service card. Shows primary owner, domain, and
 * lifecycle as compact chips. Lifecycle is color-coded since "experimental"
 * and "deprecated" are the scanning signals that matter most at the fleet
 * level. Cards without any business metadata get a single muted hint —
 * keeps the visual rhythm consistent and surfaces gaps in catalog coverage.
 */
/**
 * Business-view badges on a service card. Shows domain and lifecycle only —
 * owner is intentionally omitted here because it often coincides with domain
 * (e.g. owner=`group:payments`, domain=`payments`) and double-chipping the
 * same word reads as visual noise. The detail panel surfaces owners.
 */
function BusinessBadges({ repo: r }: { repo: RepoGraphNode }) {
  const b = r.business;
  const primaryDomain = b?.domains[0]?.value;
  const primaryLifecycle = b?.lifecycle[0]?.value;
  if (!primaryDomain && !primaryLifecycle) {
    return (
      <div className="mt-1 text-[10px] italic text-zinc-400">
        no business metadata
      </div>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {primaryDomain && (
        <span className="rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700">
          {primaryDomain}
        </span>
      )}
      {primaryLifecycle && <LifecycleBadge value={primaryLifecycle} />}
    </div>
  );
}

function LifecycleBadge({ value }: { value: string }) {
  const tone =
    value === "production"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : value === "experimental"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : value === "deprecated"
          ? "bg-red-50 text-red-700 border-red-200"
          : "bg-zinc-50 text-zinc-700 border-zinc-200";
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] ${tone}`}>
      {value}
    </span>
  );
}

const nodeTypes = { repo: RepoNode };

export function Graph() {
  const { data, isLoading, error } = useRepoGraph();
  const [searchParams] = useSearchParams();
  const slugFromUrl = searchParams.get("slug");
  const [enabledKinds, setEnabledKinds] = useState<Set<EdgeKind>>(
    new Set(EDGE_KINDS),
  );
  const [selectedNode, setSelectedNode] = useState<RepoGraphNode | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<RepoGraphEdge | null>(null);
  const [view, setView] = useState<DetailView>("technical");

  // Preselect a node when a slug is provided in the URL (e.g. when the
  // user clicks a repo citation chip from the Ask page).
  useEffect(() => {
    if (!data || !slugFromUrl) return;
    const match = data.nodes.find((n) => n.slug === slugFromUrl);
    if (match) {
      setSelectedNode(match);
      setSelectedEdge(null);
    }
  }, [data, slugFromUrl]);

  const flow = useMemo(() => {
    if (!data) return { nodes: [], edges: [] };
    return buildFlowData(data, enabledKinds, view);
  }, [data, enabledKinds, view]);

  const toggleKind = (k: EdgeKind) => {
    const next = new Set(enabledKinds);
    next.has(k) ? next.delete(k) : next.add(k);
    setEnabledKinds(next);
  };

  return (
    <div className="flex h-[calc(100vh-49px)] flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 bg-white px-6 py-3">
        <div className="flex flex-col items-start gap-1.5">
          <ViewToggle view={view} onChange={setView} />
          <div>
            <h1 className="text-base font-medium text-zinc-900">Service map</h1>
            <p className="text-xs text-zinc-500">
              {view === "technical" ? (
                <>
                  Cross-repo dependencies derived from each repo&apos;s manifest.
                  {data && (
                    <span className="ml-1">
                      {data.nodes.length} repo{data.nodes.length === 1 ? "" : "s"},{" "}
                      {flow.edges.length} edge{flow.edges.length === 1 ? "" : "s"}
                      {flow.edges.length !== data.edges.length && (
                        <span className="text-zinc-400">
                          {" "}
                          (of {data.edges.length})
                        </span>
                      )}
                    </span>
                  )}
                </>
              ) : (
                <>
                  Ownership and product context from CODEOWNERS,
                  catalog-info.yaml, and k8s metadata labels.
                  {data && (
                    <span className="ml-1">
                      {data.nodes.filter((n) => hasAnyBusinessData(n.business))
                        .length}{" "}
                      of {data.nodes.length} repo
                      {data.nodes.length === 1 ? "" : "s"} carry business
                      metadata
                    </span>
                  )}
                </>
              )}
            </p>
          </div>
        </div>
        <div
          className={`flex flex-wrap items-center gap-1 ${
            view === "business" ? "invisible" : ""
          }`}
        >
          {EDGE_KINDS.map((k) => {
            const on = enabledKinds.has(k);
            return (
              <button
                key={k}
                onClick={() => toggleKind(k)}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
                  on
                    ? "border-zinc-300 bg-white text-zinc-900"
                    : "border-zinc-200 bg-zinc-50 text-zinc-400 line-through"
                }`}
                style={
                  on
                    ? {
                        boxShadow: `inset 0 0 0 2px ${EDGE_COLORS[k] ?? "#a1a1aa"}40`,
                      }
                    : undefined
                }
              >
                {k}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 bg-zinc-50">
          {isLoading && (
            <div className="flex h-full items-center justify-center text-sm text-zinc-500">
              Loading service map…
            </div>
          )}
          {error && (
            <div className="flex h-full items-center justify-center text-sm text-red-600">
              {error instanceof Error ? error.message : "Failed to load graph"}
            </div>
          )}
          {data && data.nodes.length === 0 && <EmptyState />}
          {data && data.nodes.length > 0 && (
            <ReactFlow
              nodes={flow.nodes}
              edges={flow.edges}
              nodeTypes={nodeTypes}
              connectionMode={ConnectionMode.Loose}
              nodesDraggable
              nodesConnectable={false}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              onNodeClick={(_, n) => {
                setSelectedNode((n.data as { repo: RepoGraphNode }).repo);
                setSelectedEdge(null);
              }}
              onEdgeClick={(_, e) => {
                setSelectedEdge((e.data as { edge: RepoGraphEdge }).edge);
                setSelectedNode(null);
              }}
              onPaneClick={() => {
                setSelectedNode(null);
                setSelectedEdge(null);
              }}
            >
              <Background gap={20} size={1} color="#e4e4e7" />
              <Controls position="bottom-right" />
              <MiniMap
                pannable
                zoomable
                nodeStrokeColor="#71717a"
                nodeColor="#fafafa"
                style={{ height: 100, width: 160 }}
              />
            </ReactFlow>
          )}
        </div>

        {(selectedNode || selectedEdge) && (
          <aside className="w-80 overflow-y-auto border-l border-zinc-200 bg-white p-4">
            {selectedNode && (
              <NodeDetail node={selectedNode} graph={data} view={view} />
            )}
            {selectedEdge && <EdgeDetail edge={selectedEdge} />}
          </aside>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center px-8 text-center">
      <div className="max-w-md">
        <h2 className="text-sm font-medium text-zinc-900">No repos to map yet</h2>
        <p className="mt-1 text-xs text-zinc-500">
          Add a repository on the <span className="font-mono">Repositories</span> tab.
          Each ingest produces a manifest; edges between repos appear automatically
          once two of them share an identifier (npm package, k8s service name,
          proto package, event topic, or DB table).
        </p>
      </div>
    </div>
  );
}

function hasAnyBusinessData(b: RepoGraphBusiness | null): boolean {
  if (!b) return false;
  return (
    b.owners.length > 0 ||
    b.systems.length > 0 ||
    b.domains.length > 0 ||
    b.components.length > 0 ||
    b.lifecycle.length > 0 ||
    b.tier.length > 0 ||
    b.capabilities.length > 0
  );
}

type DetailView = "technical" | "business";

function NodeDetail({
  node,
  graph,
  view,
}: {
  node: RepoGraphNode;
  graph: RepoGraphResponse | undefined;
  view: DetailView;
}) {
  const outbound = graph?.edges.filter((e) => e.sourceSlug === node.slug) ?? [];
  const inbound = graph?.edges.filter((e) => e.targetSlug === node.slug) ?? [];
  const businessHasData = hasAnyBusinessData(node.business);

  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="text-base font-medium text-zinc-900">{node.name}</div>
        <div className="font-mono text-xs text-zinc-500">{node.slug}</div>
      </div>

      {view === "technical" ? (
        <TechnicalView node={node} outbound={outbound} inbound={inbound} />
      ) : (
        <BusinessView business={node.business} hasData={businessHasData} />
      )}
    </div>
  );
}

/**
 * Page-level toggle that drives both the card content and the sidebar
 * content together. The two views never render simultaneously — exactly the
 * separation called for in memory `project_two_view_service_map`.
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: DetailView;
  onChange: (v: DetailView) => void;
}) {
  const base = "rounded px-3 py-1 text-xs uppercase tracking-wide transition";
  const active = "bg-zinc-900 text-white";
  const inactive = "bg-transparent text-zinc-600 hover:bg-zinc-200";
  return (
    <div className="flex gap-1 rounded bg-zinc-100 p-1">
      <button
        type="button"
        className={`${base} ${view === "technical" ? active : inactive}`}
        onClick={() => onChange("technical")}
      >
        Technical
      </button>
      <button
        type="button"
        className={`${base} ${view === "business" ? active : inactive}`}
        onClick={() => onChange("business")}
      >
        Business
      </button>
    </div>
  );
}

function TechnicalView({
  node,
  outbound,
  inbound,
}: {
  node: RepoGraphNode;
  outbound: RepoGraphEdge[];
  inbound: RepoGraphEdge[];
}) {
  return (
    <div className="space-y-4">
      <DetailRow label="Team" value={node.teamName} />
      <DetailRow label="Language" value={node.language} />
      <DetailRow label="Deployment" value={node.deploymentKind} />
      {node.exposedPorts.length > 0 && (
        <DetailRow
          label="Exposed ports"
          value={node.exposedPorts.join(", ")}
        />
      )}
      <DetailRow
        label="Identifiers"
        value={
          node.hasManifest ? `${node.identifierCount} indexed` : "(no manifest)"
        }
      />
      {node.ticketCount > 0 && (
        <DetailRow label="Tickets" value={`${node.ticketCount} linked`} />
      )}

      <EdgeList title="Calls (out)" edges={outbound} side="target" />
      <EdgeList title="Called by (in)" edges={inbound} side="source" />
    </div>
  );
}

function BusinessView({
  business,
  hasData,
}: {
  business: RepoGraphBusiness | null;
  hasData: boolean;
}) {
  if (!hasData || !business) {
    return (
      <div className="rounded border border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-500">
        No business metadata extracted for this repo.
        <div className="mt-1 text-[11px] text-zinc-400">
          Sources checked: CODEOWNERS, catalog-info.yaml, k8s labels/annotations.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {business.capabilities.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-zinc-400">
            Capability
          </div>
          <ul className="space-y-1">
            {business.capabilities.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <span className="text-zinc-700">{c.summary}</span>
                <SourceBadge source={c.source} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {business.owners.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-zinc-400">
            Owners
          </div>
          <ul className="space-y-1">
            {business.owners.map((o, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono">{o.identifier}</span>
                {o.pathGlob && (
                  <span className="font-mono text-zinc-400">{o.pathGlob}</span>
                )}
                <SourceBadge source={o.source} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {business.domains.length > 0 && (
        <SourcedValueList title="Domain" items={business.domains} />
      )}
      {business.systems.length > 0 && (
        <SourcedValueList title="System" items={business.systems} />
      )}
      {business.components.length > 0 && (
        <SourcedValueList title="Type" items={business.components} />
      )}
      {business.lifecycle.length > 0 && (
        <SourcedValueList title="Lifecycle" items={business.lifecycle} />
      )}
      {business.tier.length > 0 && (
        <SourcedValueList title="Tier" items={business.tier} />
      )}
    </div>
  );
}

function SourcedValueList({
  title,
  items,
}: {
  title: string;
  items: { value: string; source: string }[];
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-400">
        {title}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            <span>{it.value}</span>
            <SourceBadge source={it.source} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span className="rounded bg-zinc-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-600">
      {source}
    </span>
  );
}

function EdgeDetail({ edge }: { edge: RepoGraphEdge }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="text-base font-medium text-zinc-900">Edge</div>
      <div className="space-y-1">
        <div>
          <span className="text-zinc-500">Kind: </span>
          <span
            className="rounded px-1.5 py-0.5 text-xs"
            style={{
              backgroundColor: `${EDGE_COLORS[edge.kind] ?? "#a1a1aa"}20`,
              color: EDGE_COLORS[edge.kind] ?? "#52525b",
            }}
          >
            {edge.kind}
          </span>
        </div>
        <div>
          <span className="text-zinc-500">Confidence: </span>
          <span>{edge.confidence}</span>
        </div>
        <div className="font-mono text-xs text-zinc-700">
          <span className="text-zinc-500">From: </span>
          {edge.sourceSlug}
        </div>
        <div className="font-mono text-xs text-zinc-700">
          <span className="text-zinc-500">To: </span>
          {edge.targetSlug}
        </div>
        <div className="text-xs">
          <span className="text-zinc-500">Signal: </span>
          <span className="font-mono">{edge.signalKind}</span>
          {" = "}
          <span className="font-mono">{edge.signalValue}</span>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-zinc-400">
        {label}
      </div>
      <div>{value}</div>
    </div>
  );
}

function EdgeList({
  title,
  edges,
  side,
}: {
  title: string;
  edges: RepoGraphEdge[];
  side: "source" | "target";
}) {
  if (edges.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-zinc-400">
        {title} ({edges.length})
      </div>
      <ul className="space-y-1">
        {edges.map((e, i) => (
          <li key={i} className="flex items-center gap-2 text-xs">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: EDGE_COLORS[e.kind] ?? "#a1a1aa" }}
            />
            <span className="font-mono">
              {side === "source" ? e.sourceSlug : e.targetSlug}
            </span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-500">{e.kind}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
